import { ActionButton } from '@emerald-works-nova/components'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Backdrop, Box, CircularProgress, Grid, Tab, Tabs, useTheme } from '@mui/material'
import { Assignment } from '@mui/icons-material'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams, generatePath } from 'react-router-dom'
import { BannerStatic, Panel, SectionHeader, TabPanel } from '../../components'
import { FolderStructureIcon } from '../../components/icons'
import { TemplateListSlice } from '../../reducers'
import DetailTab from './detail-tab'
import Structure from './structure-tab'
import { useStyles } from './style'
import { TemplateFormContext } from './template-form-context'
import { routes } from '../../routes'

const SectionNames = { details: 'details', structure: 'structure' }
const SectionMap = {
  [SectionNames.details]: {
    component: <DetailTab />,
    title: 'Details',
    icon: <Assignment />,
    next: SectionNames.structure
  },
  [SectionNames.structure]: {
    component: <Structure />,
    title: 'Structure',
    icon: <FolderStructureIcon />,
    isDisabled: ({ sk } = {}) => !sk
  }
}

const TemplateEdit = () => {
  const { spacing } = useTheme()
  const navigate = useNavigate()
  const classes = useStyles()
  const dispatch = useDispatch()
  const { id: templateSK } = useParams()
  const { state } = useLocation()
  const { parentTemplate } = state || {}
  const [template, setTemplate] = React.useState()
  const isEdit = React.useMemo(() => !!template, [template])
  const [templateHierarchy, setTemplateHierarchy] = React.useState()
  const [isLoading, setIsLoading] = React.useState(false)
  const [loadingMsg, setLoadingMsg] = React.useState()
  const [tabValue, setTabValue] = React.useState(Object.entries(SectionMap)[0][0])
  const templates = useSelector(TemplateListSlice.selectors.selectTemplateListSelect)
  const selectIsFetchingTemplateList = useSelector(TemplateListSlice.selectors.selectIsFetchingTemplateList)

  const form = useForm({ mode: 'onChange' })
  const { setValue } = form

  const [getTemplateHierarchy, templateInsert, templateUpdate, templateGet, getTemplateList] = useEvent([
    { eventName: 'catGetGroupCatByTemplate' },
    { eventName: 'templateInsert' },
    {
      eventName: 'templateUpdate',
      onSuccess: () => {
        setIsLoading(false)
        BannerStatic.show({ label: 'Template updated' })
      }
    },
    {
      eventName: 'templateGet',
      onSuccess: ({ template }) => {
        setTemplate(template)
        setIsLoading(false)
      }
    },
    TemplateListSlice.eventBus.getTemplateList
  ])

  useEventsOnViewLoad(() => {
    getTemplateHierarchy.registerAdhocOnSuccessListener(({ payload: { categories } }) => {
      setTemplateHierarchy(categories)
      setIsLoading(false)
    })

    getTemplateHierarchy.registerAdhocOnErrorListener((...args) => {
      console.log('getTemplateHierarchy.registerAdhocOnErrorListener', args)
      BannerStatic.show({ label: 'Error retriving master template', isError: true })
      setIsLoading(false)
    })
  }, [getTemplateHierarchy, setIsLoading, setLoadingMsg])

  useEventsOnViewLoad(() => {
    templateInsert.registerAdhocOnSuccessListener(({ payload: { template } }) => {
      setTemplate(template)
      setIsLoading(false)

      navigate(generatePath(routes.templateEdit.path, { id: template.sk }))
    })

    templateInsert.registerAdhocOnErrorListener((...args) => {
      console.log('templateInsert.registerAdhocOnErrorListener', args)
      BannerStatic.show({ label: 'Error creating template', isError: true })
      setIsLoading(false)
    })
  }, [templateInsert, setIsLoading, setLoadingMsg])

  React.useEffect(() => {
    if (!templates.length && !selectIsFetchingTemplateList) getTemplateList.trigger({ buildHierarchy: true })
  }, [templates, selectIsFetchingTemplateList, getTemplateList])

  React.useEffect(() => () => dispatch(TemplateListSlice.actions.resetTemplateListSelect()), [dispatch])

  const handleTabChange = React.useCallback((_, newValue) => setTabValue(newValue), [setTabValue])

  React.useEffect(() => {
    if (!template && templateSK !== 'new') {
      setIsLoading(true)
      templateGet.trigger({ id: templateSK })
      return
    }
    template && Object.entries(template).forEach(([k, v]) => setValue(k, v))
  }, [template, setValue, templateGet, templateSK])

  const handleSave = React.useCallback(async () => {
    const validation = await form.trigger()
    if (!validation) return
    const { name, parent } = form.getValues()
    setIsLoading(true)

    if (isEdit) {
      setLoadingMsg('Saving template')
      templateUpdate.trigger({ sk: template.sk, name })
      return
    }

    setLoadingMsg('Creating template')
    templateInsert.trigger({ name, parent })
  }, [isEdit, form, templateInsert, templateUpdate, template?.sk])

  const handleNext = React.useCallback(() => { setTabValue(SectionMap[tabValue].next) }, [setTabValue, tabValue])

  return (
    <TemplateFormContext.Provider value={{ form, template, setTemplate, parentTemplate, isEdit, isLoading, templateHierarchy }}>
      <Backdrop id='backdrop-loading' className={classes.backdrop} open={isLoading}>
        <Grid container direction='column' className={classes.loadingCtn} justifyContent='center' alignItems='center'>
          <Grid item><CircularProgress size={60} /></Grid>
          <Grid item className={classes.loadingMsg}>{loadingMsg}</Grid>
        </Grid>
      </Backdrop>
      <Grid container style={{ marginBottom: spacing(3) }}>
        <Grid item xs={9} data-test='title'>
          <SectionHeader title={template?.name ?? ''} location='Template /' />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {/* Template tabs */}
        <Grid item xs={12}>
          <Panel padding={0}>
            <Grid container>
              <Grid item xs={8}>
                <Tabs
                  indicatorColor='primary'
                  textColor='primary'
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label='Tenant Tabs'
                  classes={{ root: classes.tabs }}
                >
                  {Object.entries(SectionMap).map(([key, section]) => (
                    <Tab
                      id={`tab-${key}`}
                      aria-controls={`tabpanel-${key}`}
                      key={key}
                      value={key}
                      label={section.title}
                      icon={section.icon}
                      classes={{ wrapper: classes.tabWrapper }}
                      disabled={section.isDisabled && section.isDisabled(template)}
                    />
                  ))}
                </Tabs>
              </Grid>
              <Grid container item xs={4} justifyContent='flex-end' className={classes.actionBtnCtn}>
                {isEdit && (
                  <ActionButton
                    variant='secondary'
                    dataTest='template-edit-next-button'
                    onClick={handleNext}
                    className={classes.nextBtn}
                    disabled={!SectionMap[tabValue]?.next}
                  > Next
                  </ActionButton>
                )}
                <Box ml={2} />
                <ActionButton
                  variant='primary'
                  dataTest='template-edit-next-button'
                  onClick={handleSave}
                  className={classes.nextBtn}
                > Save
                </ActionButton>
              </Grid>
            </Grid>
            {/* Tabs content */}
            {Object.entries(SectionMap).map(([key]) => (
              <TabPanel key={key} value={tabValue} index={key}>
                {SectionMap[key].component}
              </TabPanel>
            ))}
          </Panel>
        </Grid>
      </Grid>
    </TemplateFormContext.Provider>
  )
}

export default TemplateEdit
